import { FormStyleContext } from "@common/contexts/FormStyle";
import { getTooltipAttributes } from "@common/contexts/Tooltip";
import { getTourAttributes } from "@common/contexts/Tour";
import Icon from "@common/controls/Icon/component";
import { type WithChildren } from "@common/declarations";
import { Common } from "@common/types";
import classnames from "classnames";
import React from "react";
import Preloader from "../Preloader/component";

import "./style.less";

export const NO_LEFT_PADDING: React.CSSProperties = {
  paddingLeft: 0
};
export type ButtonType =
  | "primary"
  | "secondary"
  | "tertiary"
  | "quaternary"
  | "danger"
  | "default"
  | "link"
  | "warning"
  | "action-link"
  | "white"
  | "action";

export type ButtonPosition = "none" | "left" | "right" | "stretch" | "center";
export type ButtonProps = Partial<WithChildren> & {
  className?: string;
  icon?: Common.IconType | string | null;
  iconPosition?: "left" | "right";
  isActive?: boolean;
  isDisabled?: boolean;
  isVisible?: boolean;
  label?: string;
  loading?: boolean;
  name?: string;
  onClick?: (e: React.MouseEvent) => void | Promise<void>;
  children?: any;
  position?: ButtonPosition;
  style?: React.CSSProperties;
  title?: any;
  tooltip?: string;
  tourStep?: string;
  type?: ButtonType;
  qa?: string;
  textAlign?: "left" | "center" | "right";

  /**
   * if specified, renders <a> tag
   */
  href?: string;
};

const Button = React.forwardRef((props: ButtonProps, ref: React.RefObject<any>) => {
  const { primaryButtonStyle, secondaryButtonStyle } = React.useContext(FormStyleContext);

  const style = React.useMemo(
    () => ({
      ...((props.type ?? "primary") === "primary"
        ? primaryButtonStyle
        : props.type === "link"
        ? undefined
        : secondaryButtonStyle),
      ...props.style,
      justifyContent:
        props.textAlign === "right"
          ? "flex-end"
          : props.textAlign === "center"
          ? "center"
          : props.textAlign === "left"
          ? "flex-start"
          : props.style?.justifyContent ?? "center"
    }),
    [props.style, props.type, props.textAlign, primaryButtonStyle, secondaryButtonStyle]
  );

  if (props.isVisible === false) return null;

  const Tag = props.href ? "a" : "button";
  const baseProps = props.href ? { href: props.href } : {};

  const isDisabled = props.isDisabled || props.loading;

  return (
    <Tag
      {...baseProps}
      ref={ref}
      style={style}
      title={props.title}
      onClick={isDisabled ? undefined : props.onClick}
      type="button"
      className={classnames(
        "btn",
        `btn-${props.type || "primary"}`,
        {
          disabled: isDisabled,
          active: props.isActive
        },
        props.position,
        props.className
      )}
      disabled={isDisabled}
      data-name={props.name}
      {...getTooltipAttributes(props.tooltip)}
      {...getTourAttributes({ step: props.tourStep })}
    >
      {(props.loading || !!props.icon) &&
        (props.iconPosition || "left") === "left" &&
        (props.loading ? (
          <Preloader color="white" isFontSize relative />
        ) : (
          <Icon className={classnames("fa-btn-icon")} icon={props.icon!} />
        ))}
      {(props.label || props.children) && <span>{props.label || props.children}</span>}
      {(props.loading || !!props.icon) &&
        props.iconPosition === "right" &&
        (props.loading ? (
          <Preloader color="white" isFontSize />
        ) : (
          <Icon className={classnames("fa-btn-icon")} icon={props.icon!} />
        ))}
    </Tag>
  );
});

export default Button;

export const AsyncButton = (props: ButtonProps) => {
  const [isBusy, setIsBusy] = React.useState(false);

  const onClick = React.useCallback(async (e: React.MouseEvent) => {
    const r = props.onClick!(e);
    if (r instanceof Promise) {
      setIsBusy(true);
      try {
        return await r;
      } finally {
        setIsBusy(false);
      }
    }
  }, []);

  return <Button {...props} isDisabled={isBusy || props.isDisabled} onClick={props.onClick ? onClick : undefined} />;
};
